/**
 * Cadastro assistido de operadora.
 *
 * Fluxo desenhado com o cliente: o usuário sobe um arquivo-exemplo, a IA
 * infere a **especificação de leitura** (nunca os dados), o motor
 * determinístico aplica essa spec e tenta fechar com os totais impressos no
 * próprio documento, e só então o usuário confere a tabulação e envia ao
 * administrador.
 *
 * Duas coisas que a tela precisa deixar explícitas, porque são decisões de
 * projeto e não detalhes:
 *
 * 1. **A aprovação é aritmética, não visual.** A pré-visualização nunca
 *    aparece sozinha — vem sempre acompanhada do resultado das conferências.
 *    Ninguém confere milhares de linhas no olho.
 * 2. **Só a estrutura sai daqui.** Os arquivos têm nome de paciente e
 *    carteirinha. O que vai para a IA são coordenadas e tipos de campo, com os
 *    valores mascarados.
 */
import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { mensagemErro } from '../api/axios';
import {
  iaDisponivel,
  ajustarRelacaoConjunto,
  inferirConjunto,
  inferirModelo,
  retentarInferencia,
  submeterModelo,
  submeterConjunto,
} from '../api/fluxfin';
import {
  Alerta,
  Badge,
  Botao,
  Indicador,
  LoadingSpinner,
  PageHeader,
  Painel,
  Tabela,
  Th,
} from '../components/common';
import { Conferencias, Diagnostico, LeituraDaIA } from '../components/LaudoModelo';
import { brl, numero } from '../utils/formato';

/** `total_pago_impresso` -> `Total pago impresso`. */
function rotuloTotal(chave) {
  const t = chave.replace(/_/g, ' ');
  return t.charAt(0).toUpperCase() + t.slice(1);
}

/* ── Amostra da tabulação ──────────────────────────────────────────────── */
function Tabulacao({ diagnostico }) {
  const amostra = diagnostico.amostra_guias || [];
  const resumo = diagnostico.resumo || {};
  // O total impresso no documento e o que prova a leitura. `total_extraido`
  // vem repetido aqui pelo motor — o que interessa sao os outros.
  const impressos = Object.entries(diagnostico.totais_documento || {}).filter(
    ([chave]) => chave !== 'total_extraido'
  );

  return (
    <Painel
      titulo="Pré-visualização"
      subtitulo={`Primeiras ${amostra.length} guias lidas com esta especificação.`}
    >
      <div className="ff-indicadores p-4 pb-0">
        <Indicador rotulo="Guias" valor={numero(resumo.guias)} />
        <Indicador rotulo="Linhas" valor={numero(resumo.linhas)} />
        <Indicador
          rotulo="Soma extraída"
          valor={brl(resumo.total_extraido)}
          nota="soma das linhas de procedimento"
        />
        {impressos.map(([chave, valor]) => (
          <Indicador
            key={chave}
            rotulo={rotuloTotal(chave)}
            valor={brl(valor)}
            nota="impresso no próprio arquivo"
            natureza={
              String(valor) === String(resumo.total_extraido) ? 'positivo' : 'atencao'
            }
          />
        ))}
      </div>

      {amostra.length > 0 && (
        <Tabela estreita>
          <thead>
            <tr>
              <Th>Guia</Th>
              <Th>Lote</Th>
              <Th alinhar="right">Linhas</Th>
              <Th alinhar="right">Soma das linhas</Th>
              <Th alinhar="right">Subtotal impresso</Th>
              <Th>Confere</Th>
            </tr>
          </thead>
          <tbody>
            {amostra.map((g) => (
              <tr key={g.guia}>
                <td className="col-principal ff-mono">{g.guia}</td>
                <td className="col-meta ff-mono">{g.lote}</td>
                <td className="col-qtd">{g.qtd_linhas}</td>
                <td className="col-valor">{brl(g.soma_linhas)}</td>
                <td className="col-valor">{brl(g.subtotal_impresso)}</td>
                <td>
                  <Badge tom={g.confere ? 'confirmado' : 'reprovado'}>
                    {g.confere ? 'Sim' : 'Não'}
                  </Badge>
                </td>
              </tr>
            ))}
          </tbody>
        </Tabela>
      )}
    </Painel>
  );
}

/* ── O conjunto: N modelos e a relação entre eles ──────────────────────
 *
 * A relação não é detalhe de organização. `equivalentes` liga uma **trava**:
 * o segundo arquivo passa a ser recusado no lançamento, porque o hash não
 * protege contra o mesmo conteúdo em outra roupa — bytes diferentes, hashes
 * diferentes, guias contadas duas vezes, baixa dobrada. E nenhuma conferência
 * aritmética pegaria, porque cada arquivo fecha sozinho, perfeitamente.
 *
 * Por isso a tela deixa a classificação **editável** e explica de onde ela
 * veio: das duas leituras concordando, de uma só, ou da divergência entre
 * elas.
 */
function ResultadoConjunto({ dados, aoVoltar, aoFalhar }) {
  const [relacao, setRelacao] = useState(dados.conjunto.relacao);
  const [salvando, setSalvando] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [enviado, setEnviado] = useState(false);
  const { conjunto, modelos, falhas } = dados;

  const trocar = async (nova) => {
    setSalvando(true);
    try {
      await ajustarRelacaoConjunto(conjunto.id_conjunto, nova);
      setRelacao(nova);
    } catch (e) {
      aoFalhar(mensagemErro(e, 'Não foi possível alterar a relação.'));
    } finally {
      setSalvando(false);
    }
  };

  const enviar = async () => {
    setEnviando(true);
    try {
      await submeterConjunto(conjunto.id_conjunto);
      setEnviado(true);
    } catch (e) {
      aoFalhar(mensagemErro(e, 'Não foi possível enviar para aprovação.'));
    } finally {
      setEnviando(false);
    }
  };

  const equivalente = relacao === 'equivalentes';
  const algumReprovou = modelos.some((m) => !m.diagnostico.aprovado);

  return (
    <>
      <Painel
        titulo={`Conjunto: ${conjunto.nome}`}
        subtitulo={`${modelos.length} arquivo(s) mapeado(s)`}
        acoes={
          <Badge tom={conjunto.incerto ? 'pendente' : 'confirmado'}>
            {conjunto.incerto ? 'Confirme a relação' : 'Classificado'}
          </Badge>
        }
      >
        <div className="p-4">
          <div
            className={`ff-aviso ff-aviso--${conjunto.incerto ? 'atencao' : 'info'}`}
            style={{ margin: '0 0 16px' }}
          >
            <i
              className={`bi bi-${conjunto.incerto ? 'exclamation-triangle' : 'diagram-3'}`}
              aria-hidden="true"
            />
            <div>{conjunto.raciocinio}</div>
          </div>

          <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 8 }}>
            Como estes arquivos se relacionam
          </div>
          <div className="d-flex flex-column gap-2">
            {[
              {
                valor: 'complementares',
                titulo: 'Se completam — envie todos',
                texto:
                  'Cada arquivo traz uma informação diferente, como o demonstrativo de pagamento e o extrato de glosa. O sistema espera os dois e avisa quando faltar um.',
              },
              {
                valor: 'equivalentes',
                titulo: 'Mesmo conteúdo em formatos diferentes — envie um',
                texto:
                  'Os arquivos trazem as mesmas guias. O sistema aceita o primeiro que chegar e recusa o outro: aceitar os dois contaria cada guia duas vezes e dobraria a baixa.',
              },
            ].map((o) => (
              <label
                key={o.valor}
                className="d-flex align-items-start gap-2 p-3"
                style={{
                  border: `1px solid ${relacao === o.valor ? 'var(--ff-acento)' : 'var(--ff-border-soft)'}`,
                  borderRadius: 10,
                  cursor: 'pointer',
                }}
              >
                <input
                  type="radio"
                  name="relacao"
                  checked={relacao === o.valor}
                  onChange={() => trocar(o.valor)}
                  disabled={salvando}
                  style={{ marginTop: 3 }}
                />
                <span>
                  <strong>{o.titulo}</strong>
                  <span style={{ display: 'block', fontSize: 13, color: 'var(--ff-muted)' }}>
                    {o.texto}
                  </span>
                </span>
              </label>
            ))}
          </div>

          {equivalente && (
            <p style={{ fontSize: 13, color: '#9a5b06', marginTop: 10, marginBottom: 0 }}>
              <i className="bi bi-shield-check me-1" aria-hidden="true" />
              Com esta classificação, enviar o segundo arquivo da mesma competência será
              recusado — é a trava que impede a baixa em duplicidade.
            </p>
          )}
        </div>
      </Painel>

      {falhas.length > 0 && (
        <div className="mt-3">
          <Alerta tipo="atencao">
            <strong>{falhas.length} arquivo(s) não puderam ser mapeados</strong> e ficaram
            de fora do conjunto. Os demais seguiram normalmente.
            <ul className="mb-0 mt-2">
              {falhas.map((f) => (
                <li key={f.arquivo}>
                  <span className="ff-mono">{f.arquivo}</span> —{' '}
                  {typeof f.erro === 'string' ? f.erro : f.erro?.mensagem || 'erro'}
                </li>
              ))}
            </ul>
          </Alerta>
        </div>
      )}

      {modelos.map((m) => (
        <div key={m.modelo.id_modelo_arquivo} className="mt-3">
          <Painel
            titulo={m.modelo.arquivo_exemplo_nome}
            subtitulo={`${m.modelo.formato} · ${PAPEL_ROTULO[m.papel] || m.papel}`}
            acoes={
              <Badge tom={m.diagnostico.aprovado ? 'confirmado' : 'reprovado'}>
                {m.diagnostico.aprovado ? 'Conferências fecharam' : 'Não fechou'}
              </Badge>
            }
          >
            <div className="p-4">
              <Diagnostico diagnostico={m.diagnostico} />
              <div className="mt-3">
                <Conferencias diagnostico={m.diagnostico} comPainel={false} />
              </div>
            </div>
          </Painel>
        </div>
      ))}

      {enviado ? (
        <div className="mt-3">
          <Alerta tipo="ok">
            <strong>Enviado para aprovação.</strong> Os {modelos.length} modelos do
            conjunto estão na aba <strong>Aguardando aprovação</strong>. A operadora só
            aparece na tela de lançamento depois que um administrador aprovar.
          </Alerta>
          <div className="d-flex gap-2 mt-3">
            <Botao variante="primario" onClick={aoVoltar}>
              Ir para os modelos
            </Botao>
          </div>
        </div>
      ) : (
        <>
          {algumReprovou && (
            <div className="mt-3">
              <Alerta tipo="atencao">
                Um ou mais arquivos <strong>não fecharam as conferências</strong>. Você
                ainda pode enviar — o administrador verá o laudo completo e decide. Se
                aprovar assim mesmo, o modelo fica permanentemente marcado como{' '}
                <strong>não verificado aritmeticamente</strong>, e a marca acompanha todo
                dado extraído por ele.
              </Alerta>
            </div>
          )}
          <div className="d-flex gap-2 mt-3">
            <Botao variante="primario" onClick={enviar} disabled={enviando}>
              {enviando ? 'Enviando…' : `Enviar os ${modelos.length} para aprovação`}
            </Botao>
            <Botao onClick={aoVoltar} disabled={enviando}>
              Deixar como rascunho
            </Botao>
          </div>
          <p style={{ fontSize: 13, color: 'var(--ff-muted)', marginTop: 8 }}>
            Confira a relação e o laudo de cada arquivo acima. O conjunto vai{' '}
            <strong>inteiro</strong>: aprovar só parte dele deixaria a operadora
            funcionando pela metade, recusando no lançamento o arquivo que ficou para
            trás. Deixando como rascunho, dá para enviar depois pela tela de Modelos.
          </p>
        </>
      )}
    </>
  );
}

const PAPEL_ROTULO = {
  recebimento: 'traz o pagamento',
  glosa: 'traz a glosa',
  recebimento_e_glosa: 'traz pagamento e glosa',
};

export default function ModeloNovo() {
  const navigate = useNavigate();
  // Vem preenchido quando a pessoa clicou em "Nova versão" na tela de
  // modelos: a operadora ja e conhecida, e so o layout mudou.
  const { state } = useLocation();

  const [ia, setIa] = useState(null);
  const [carregando, setCarregando] = useState(true);

  const [nomeOperadora, setNomeOperadora] = useState(state?.operadora || '');
  const [nomeModelo, setNomeModelo] = useState(state?.novaVersaoDe || '');
  const [observacao, setObservacao] = useState('');
  // Lista, não arquivo único: a Unimed manda o demonstrativo de pagamento e o
  // extrato de glosa, e é preciso cadastrar os dois juntos para o sistema
  // saber que um completa o outro.
  const [arquivos, setArquivos] = useState([]);
  const [nomeConjunto, setNomeConjunto] = useState('');

  const [processando, setProcessando] = useState(false);
  const [progresso, setProgresso] = useState(0);
  const [erro, setErro] = useState('');
  const [resultado, setResultado] = useState(null);
  const [conjunto, setConjunto] = useState(null);
  const [enviando, setEnviando] = useState(false);

  useEffect(() => {
    iaDisponivel()
      .then((r) => setIa(r.data))
      .catch(() => setIa({ disponivel: false }))
      .finally(() => setCarregando(false));
  }, []);

  async function analisar(e) {
    e.preventDefault();
    if (arquivos.length === 0 || !nomeOperadora.trim()) return;
    setErro('');
    setProcessando(true);
    setProgresso(0);
    try {
      if (arquivos.length === 1) {
        // Um arquivo não forma conjunto — não há relação a classificar, e o
        // caminho simples devolve a tela de conferência direto.
        const r = await inferirModelo({
          arquivo: arquivos[0],
          nomeOperadora: nomeOperadora.trim(),
          nomeModelo: nomeModelo.trim() || undefined,
          observacao: observacao.trim() || undefined,
          onProgress: setProgresso,
        });
        setResultado(r.data);
      } else {
        const r = await inferirConjunto({
          arquivos,
          nomeOperadora: nomeOperadora.trim(),
          nomeConjunto: nomeConjunto.trim() || undefined,
          observacao: observacao.trim() || undefined,
          onProgress: setProgresso,
        });
        setConjunto(r.data);
      }
    } catch (err) {
      setErro(mensagemErro(err, 'Não foi possível analisar os arquivos.'));
    } finally {
      setProcessando(false);
    }
  }

  async function retentar() {
    setErro('');
    setProcessando(true);
    try {
      const r = await retentarInferencia(
        resultado.modelo.id_modelo_arquivo,
        observacao.trim() || undefined
      );
      setResultado(r.data);
    } catch (err) {
      setErro(mensagemErro(err, 'A nova tentativa não deu certo.'));
    } finally {
      setProcessando(false);
    }
  }

  async function enviarAoAdmin() {
    setErro('');
    setEnviando(true);
    try {
      await submeterModelo(resultado.modelo.id_modelo_arquivo, observacao.trim() || undefined);
      navigate('/modelos', { state: { submetido: true } });
    } catch (err) {
      setErro(mensagemErro(err, 'Não foi possível enviar para aprovação.'));
      setEnviando(false);
    }
  }

  if (carregando) return <LoadingSpinner />;

  if (!ia?.disponivel) {
    return (
      <>
        <PageHeader
          titulo="Cadastrar operadora"
          acoes={
            <Botao variante="secundario" onClick={() => navigate('/modelos')}>
              Voltar
            </Botao>
          }
        />
        <Alerta tipo="atencao">
          O cadastro assistido está indisponível neste ambiente: falta configurar a chave da
          OpenAI. Um administrador precisa preencher <code>OPENAI_API_KEY</code> no{' '}
          <code>.env</code>. Enquanto isso, modelos continuam podendo ser cadastrados
          manualmente por um administrador.
        </Alerta>
      </>
    );
  }

  const diagnostico = resultado?.diagnostico;
  const fechou = diagnostico?.aprovado;
  const tentativas = resultado?.modelo?.tentativas_ia ?? 0;
  const noLimite = tentativas >= (ia.max_tentativas ?? 3);

  return (
    <>
      <PageHeader
        titulo={state?.novaVersaoDe ? 'Nova versão do modelo' : 'Cadastrar operadora'}
        subtitulo="Suba um demonstrativo de exemplo. A IA descobre como lê-lo; o sistema prova se acertou. Aceita PDF, planilha e XML."
        acoes={
          <Botao variante="secundario" onClick={() => navigate('/modelos')}>
            Voltar
          </Botao>
        }
      />

      <Alerta tipo="erro" onFechar={() => setErro('')}>
        {erro}
      </Alerta>

      {state?.novaVersaoDe && !resultado && !conjunto && (
        <Alerta tipo="info">
          Nova versão de <strong>{state.novaVersaoDe}</strong>. A versão atual continua
          valendo até que esta seja aprovada — arquivos já recebidos não são afetados.
        </Alerta>
      )}

      {!resultado && !conjunto && (
        <Painel>
          <form onSubmit={analisar} className="p-4 d-flex flex-column gap-3">
            <div className="ff-campo">
              <label htmlFor="operadora">Operadora</label>
              <input
                id="operadora"
                className="ff-input"
                value={nomeOperadora}
                onChange={(e) => setNomeOperadora(e.target.value)}
                placeholder="GEAP, Sulamérica…"
                required
                disabled={processando}
              />
              <span style={{ fontSize: 13, color: 'var(--ff-muted-2)' }}>
                Se já existir uma operadora com este nome, o modelo entra como nova versão
                dela.
              </span>
            </div>

            <div className="ff-campo">
              <label htmlFor="nome-modelo">Nome do relatório (opcional)</label>
              <input
                id="nome-modelo"
                className="ff-input"
                value={nomeModelo}
                onChange={(e) => setNomeModelo(e.target.value)}
                placeholder="Demonstrativo de Pagamento"
                disabled={processando}
              />
              <span style={{ fontSize: 13, color: 'var(--ff-muted-2)' }}>
                Em branco, vale o nome que a IA identificar no próprio documento.
              </span>
            </div>

            <div className="ff-campo">
              <label htmlFor="arquivo">Arquivos de exemplo</label>
              <input
                id="arquivo"
                type="file"
                multiple
                className="form-control"
                style={{ borderRadius: 8, borderColor: 'var(--ff-border-field)' }}
                accept=".pdf,.xlsx,.xlsm,.xml"
                onChange={(e) => {
                  const novos = Array.from(e.target.files || []);
                  setArquivos((atual) => {
                    const jaTem = new Set(atual.map((f) => `${f.name}|${f.size}`));
                    return [
                      ...atual,
                      ...novos.filter((f) => !jaTem.has(`${f.name}|${f.size}`)),
                    ].slice(0, 6);
                  });
                  e.target.value = '';
                }}
                disabled={processando}
              />
              <span style={{ fontSize: 13, color: 'var(--ff-muted-2)' }}>
                PDF, planilha (.xlsx) ou XML. Demonstrativos reais e, de preferência,
                completos — é dos totais impressos neles que sai a prova de que a leitura
                está certa.
              </span>
            </div>

            {arquivos.length > 0 && (
              <div className="ff-campo" style={{ margin: 0 }}>
                <label>Arquivos selecionados</label>
                <div className="d-flex flex-column gap-2">
                  {arquivos.map((f, i) => (
                    <div
                      key={`${f.name}-${f.size}`}
                      className="d-flex align-items-center gap-2 px-3 py-2"
                      style={{
                        border: '1px solid var(--ff-border-soft)',
                        borderRadius: 8,
                      }}
                    >
                      <i className="bi bi-file-earmark-text" aria-hidden="true" />
                      <span className="ff-mono" style={{ flex: 1, fontSize: 13 }}>
                        {f.name}
                      </span>
                      <Botao
                        variante="secundario"
                        pequeno
                        onClick={() =>
                          setArquivos((a) => a.filter((_, idx) => idx !== i))
                        }
                        disabled={processando}
                      >
                        Remover
                      </Botao>
                    </div>
                  ))}
                </div>
                {arquivos.length > 1 && (
                  <span style={{ fontSize: 13, color: 'var(--ff-muted-2)' }}>
                    Serão mapeados um a um, e o sistema vai classificar se{' '}
                    <strong>se completam</strong> (como pagamento e glosa da Unimed) ou se
                    são o <strong>mesmo conteúdo em formatos diferentes</strong> (como o
                    XLSX e o XML da GEAP).
                  </span>
                )}
              </div>
            )}

            {arquivos.length > 1 && (
              <div className="ff-campo">
                <label htmlFor="nome-conjunto">Nome do conjunto (opcional)</label>
                <input
                  id="nome-conjunto"
                  className="ff-input"
                  placeholder="Ex.: Demonstrativo e glosa"
                  value={nomeConjunto}
                  onChange={(e) => setNomeConjunto(e.target.value)}
                  disabled={processando}
                />
                <span style={{ fontSize: 13, color: 'var(--ff-muted-2)' }}>
                  Em branco, o sistema sugere um a partir do que os arquivos são.
                </span>
              </div>
            )}

            <div className="ff-campo">
              <label htmlFor="observacao">Observação para a IA (opcional)</label>
              <textarea
                id="observacao"
                className="ff-input"
                rows={2}
                value={observacao}
                onChange={(e) => setObservacao(e.target.value)}
                placeholder="Ex.: o número da guia tem 13 dígitos e vem depois do lote."
                disabled={processando}
              />
              <span style={{ fontSize: 13, color: 'var(--ff-muted-2)' }}>
                Qualquer coisa que ajude a entender o documento.
              </span>
            </div>

            <Alerta tipo="info">
              <strong>Uma amostra do documento é enviada à IA</strong> — algumas páginas
              com o texto real e as coordenadas, não o arquivo inteiro. É o que permite a
              ela ler os rótulos das colunas e os marcadores de total; com o conteúdo
              mascarado, o mapeamento não funcionava. A conta usada é privada, sem uso dos
              dados para treino e com exclusão em 30 dias. <strong>O que saiu fica guardado
              e visível ao administrador</strong>, na tela de aprovação deste modelo.
            </Alerta>

            {processando && (
              <div>
                <div className="progress" style={{ height: 6, borderRadius: 3 }}>
                  <div
                    className="progress-bar"
                    style={{
                      width: `${progresso < 100 ? progresso : 100}%`,
                      background: 'var(--ff-blue)',
                    }}
                  />
                </div>
                <small style={{ color: 'var(--ff-muted-2)' }}>
                  {progresso < 100
                    ? `Enviando… ${progresso}%`
                    : 'A IA está mapeando o layout e o motor vai conferir os totais — costuma levar de 30s a 2 min.'}
                </small>
              </div>
            )}

            <div>
              <button
                type="submit"
                className="ff-btn ff-btn--primario"
                disabled={
                  processando || arquivos.length === 0 || !nomeOperadora.trim()
                }
              >
                {processando
                  ? 'Analisando…'
                  : `Analisar ${arquivos.length > 1 ? `${arquivos.length} arquivos` : 'arquivo'}`}
              </button>
            </div>
          </form>
        </Painel>
      )}

      {conjunto && (
        <ResultadoConjunto
          dados={conjunto}
          aoVoltar={() => navigate('/modelos')}
          aoFalhar={setErro}
        />
      )}

      {resultado && (
        <>
          <Alerta tipo={fechou ? 'ok' : 'atencao'}>
            {fechou ? (
              <>
                <strong>A extração fecha com os totais do documento.</strong> Confira a
                tabulação abaixo e envie para aprovação do administrador.
              </>
            ) : (
              <>
                <strong>Erro de identificação do relatório.</strong> As conferências não
                fecharam, então este mapeamento ainda não é confiável. Você pode tentar de
                novo (tentativa {tentativas} de {ia.max_tentativas}) ou enviar assim mesmo — o
                administrador verá o diagnóstico completo e decidirá.
              </>
            )}
          </Alerta>

          <Diagnostico diagnostico={diagnostico} />
          <div className="mt-3">
            <Conferencias diagnostico={diagnostico} />
          </div>
          <div className="mt-3">
            <Tabulacao diagnostico={diagnostico} />
          </div>
          <div className="mt-3">
            <LeituraDaIA diagnostico={diagnostico} />
          </div>

          <div className="d-flex flex-wrap gap-2 mt-3">
            <button
              type="button"
              className="ff-btn ff-btn--primario"
              onClick={enviarAoAdmin}
              disabled={enviando || processando}
            >
              {enviando ? 'Enviando…' : 'Enviar para aprovação'}
            </button>
            <button
              type="button"
              className="ff-btn ff-btn--secundario"
              onClick={retentar}
              disabled={processando || enviando || noLimite}
            >
              {processando ? 'Mapeando…' : 'Tentar mapear de novo'}
            </button>
            <Botao variante="secundario" onClick={() => navigate('/modelos')}>
              Deixar como rascunho
            </Botao>
          </div>

          {noLimite && (
            <p className="mt-2" style={{ fontSize: 13, color: 'var(--ff-muted)' }}>
              Limite de {ia.max_tentativas} tentativas atingido para este arquivo. O limite
              existe porque um mapeamento pode simplesmente não existir para este layout — um
              administrador pode ajustar a especificação à mão, ou você pode recomeçar com
              outro exemplo.
            </p>
          )}
        </>
      )}
    </>
  );
}
